import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, UserPlus, Star, X, MapPin, Phone } from "lucide-react";
import ViewToggle, { useViewMode } from "@/components/admin/ViewToggle";
import { useLang } from "@/lib/lang";

const STATUS_TABS = [
  { key: "all", labelKey: null, label: "todos" },
  { key: "pending", labelKey: "status_pending" },
  { key: "approved", labelKey: "status_approved" },
  { key: "suspended", labelKey: "status_suspended" },
  { key: "rejected", labelKey: "status_rejected" },
];

export default function Drivers() {
  const { t } = useLang();
  const [drivers, setDrivers] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [mode, setMode] = useViewMode("drivers", "list");

  const load = () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/admin/drivers?${params.toString()}`).then((r) => setDrivers(r.data));
  };

  useEffect(load, [status, q]); // eslint-disable-line

  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader eyebrow="Frota · Pessoal" title={t("nav_drivers")} description="Aprovar, suspender e configurar cada motorista da plataforma.">
        <ViewToggle scope="drivers" value={mode} onChange={setMode} testId="drivers-view-toggle" />
        <GoldButton data-testid="new-driver-btn" onClick={() => setShowCreate(true)}><UserPlus className="w-4 h-4" /> {t("add")}</GoldButton>
      </PageHeader>

      {/* Filters */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex gap-1 flex-wrap">
          {STATUS_TABS.map((tk) => (
            <button
              key={tk.key}
              data-testid={`filter-${tk.key}`}
              onClick={() => setStatus(tk.key)}
              className={`px-4 py-2 text-xs uppercase tracking-wide border font-semibold ${
                status === tk.key ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-600 hover:text-[#0a0f1e]"
              }`}
            >{tk.labelKey ? t(tk.labelKey) : tk.label}</button>
          ))}
        </div>
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid="drivers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search") + "…"}
            className="pl-10 h-10 bg-transparent border-slate-200 rounded-none focus:border-[#f59e0b]"
          />
        </div>
      </div>

      {drivers.length === 0 && (
        <div className="border border-dashed border-slate-200 p-16 text-center text-slate-500">Nenhum motorista encontrado.</div>
      )}

      {/* CARDS view (detailed) */}
      {mode === "cards" && drivers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="drivers-cards-view">
          {drivers.map((d) => (
            <div key={d.id} data-testid={`driver-card-${d.id}`} className="border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center text-[#f59e0b] font-display text-lg">
                    {d.name?.[0] || "D"}
                  </div>
                  <div>
                    <div className="text-[#0a0f1e] font-semibold">{d.name}</div>
                    <div className="flex items-center gap-1 text-xs">
                      <Star className="w-3 h-3 text-[#f59e0b] fill-[#f59e0b]" />
                      <span className="text-[#0a0f1e] font-bold">{d.rating_avg?.toFixed(1) || "—"}</span>
                      <span className="text-slate-500">({d.trips_count})</span>
                    </div>
                  </div>
                </div>
                <StatBadge status={d.status} />
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2 text-slate-700"><Phone className="w-3 h-3 text-slate-500" /> {d.phone || "—"}</div>
                <div className="text-slate-700 truncate">{d.email}</div>
                <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-3 h-3 text-slate-500" /> {(d.zones_covered || []).join(", ") || "—"}</div>
                <div className="text-slate-700">{(d.languages || []).join(" · ")}</div>
                <div className="pt-2 flex items-center justify-between text-slate-700">
                  <span>€{d.pricing_km}/km · €{d.pricing_hour}/h</span>
                  {d.fleet_id ? (
                    <span className="text-[10px] uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 font-bold">FROTA</span>
                  ) : (
                    <span className="text-[10px] uppercase text-slate-700 bg-slate-100 px-1.5 py-0.5 font-bold">INDEP.</span>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                <StatBadge status={d._document_status} />
                <Link to={`/admin/drivers/${d.id}`} data-testid={`open-driver-card-${d.id}`} className="text-xs uppercase tracking-wide text-[#f59e0b] hover:underline font-bold">
                  {t("action_open")}
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LIST view (compact table) */}
      {mode === "list" && drivers.length > 0 && (
      <div className="border border-slate-200 bg-white" data-testid="drivers-list-view">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left label-caps">
              <th className="p-4">{t("nav_drivers")}</th>
              <th className="p-4">{t("label_contact")}</th>
              <th className="p-4">{t("label_languages")}</th>
              <th className="p-4">{t("label_zones")}</th>
              <th className="p-4">Veículo</th>
              <th className="p-4">{t("label_rating")}</th>
              <th className="p-4">{t("nav_documents")}</th>
              <th className="p-4">{t("label_status")}</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((d) => (
              <tr key={d.id} data-testid={`driver-row-${d.id}`} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-none bg-white border border-slate-200 flex items-center justify-center text-[#f59e0b] font-display">
                      {d.name?.[0] || "D"}
                    </div>
                    <div>
                      <div className="text-[#0a0f1e] font-semibold">{d.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{d.id.slice(0, 8)}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-slate-700">
                  <div className="text-xs">{d.email}</div>
                  <div className="text-xs text-slate-500">{d.phone}</div>
                </td>
                <td className="p-4 text-xs text-slate-700">{(d.languages || []).join(" · ")}</td>
                <td className="p-4 text-xs text-slate-700">{(d.zones_covered || []).join(", ")}</td>
                <td className="p-4 text-xs">
                  {d._primary_vehicle ? (
                    <div>
                      <div className="text-[#0a0f1e] font-semibold">{d._primary_vehicle.brand} {d._primary_vehicle.model}</div>
                      <div className="text-slate-500 font-mono">{d._primary_vehicle.plate}</div>
                    </div>
                  ) : (
                    <span className="text-red-600 font-semibold">Sem veículo</span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-[#f59e0b] fill-[#f59e0b]" />
                    <span className="text-[#0a0f1e] text-xs font-bold">{d.rating_avg?.toFixed(1) || "—"}</span>
                    <span className="text-slate-500 text-xs">({d.trips_count})</span>
                  </div>
                </td>
                <td className="p-4"><StatBadge status={d._document_status} /></td>
                <td className="p-4"><StatBadge status={d.status} /></td>
                <td className="p-4">
                  <Link to={`/admin/drivers/${d.id}`} data-testid={`open-driver-${d.id}`} className="text-xs uppercase tracking-wide text-[#f59e0b] hover:underline font-bold">
                    Abrir →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {showCreate && <CreateDriverModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateDriverModal({ onClose, onCreated }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/drivers", form);
      toast.success("Driver invited");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-md p-8" data-testid="create-driver-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="label-caps text-[#f59e0b] mb-1">{t("action_invite")}</p>
            <h3 className="font-display text-2xl text-[#0a0f1e]">{t("drivers_new")}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-[#0a0f1e]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          {["name", "email", "phone", "password"].map((k) => (
            <div key={k}>
              <label className="label-caps block mb-2">{k}</label>
              <Input
                data-testid={`driver-input-${k}`}
                type={k === "password" ? "password" : "text"}
                required={k !== "phone"}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="h-11 bg-transparent border-slate-200 rounded-none focus:border-[#f59e0b]"
              />
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-8">
          <GhostButton type="button" onClick={onClose} className="flex-1">{t("action_cancel")}</GhostButton>
          <GoldButton type="submit" disabled={loading} className="flex-1 justify-center" data-testid="submit-driver">
            {loading ? t("action_creating") : t("action_create")}
          </GoldButton>
        </div>
      </form>
    </div>
  );
}
